// src/app/components/reporting/report-generator-page.tsx
import { useState, useEffect, useMemo } from 'react';
import {
  Sparkles, Loader2, CheckCircle2, Clock,
  ChevronDown, ChevronUp, Search, FileText, AlertCircle,
} from 'lucide-react';
import { UnderlineTabs, UnderlineTabsPanel } from '../ui/underline-tabs';
import { AIAssistantSidebar } from '../ai-assistant-sidebar';
import { InsightsList }    from './components/insights-list';
import { ReportComposer }  from './components/report-composer';
import { ReportOutput }    from './components/report-output';
import { ExportActions }   from './components/export-actions';
import { useInsights }     from '../../hooks/reporting/useInsights';
import { useGenerateReport, useReportHistory } from '../../hooks/reporting/useReportGenerator';
import { MODULE_COLORS }   from './types';
import type { GeneratedReport, ReportSection } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Count chip — matches prompt-tracking/action-log style for tab labelExtra
// ─────────────────────────────────────────────────────────────────────────────

function CountChip({ count, active }: { count: number; active: boolean }) {
  return (
    <span
      className="flex items-center justify-center rounded-full min-w-[20px] h-5 px-1.5"
      style={{
        backgroundColor: active ? 'var(--alpha-teal)' : 'var(--muted-foreground)',
        color: 'white',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
      }}
    >
      {count}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// History card — expandable read-only view of one past report
// ─────────────────────────────────────────────────────────────────────────────

function HistoryCard({ report }: { report: GeneratedReport }) {
  const [expanded, setExpanded] = useState(false);
  const date      = new Date(report.created_at);
  const dateLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const timeLabel = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      className="rounded-2xl overflow-hidden transition-all"
      style={{
        backgroundColor: 'var(--card)',
        border: expanded ? '1px solid var(--alpha-teal)' : '1px solid var(--border)',
        boxShadow: 'var(--elevation-sm)',
      }}
    >
      {/* Collapsed header */}
      <div className="p-4 md:p-5">
        {/* Header row — stacks on mobile (meta group above, date/button block below),
            side-by-side on sm+ */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-3 mb-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'var(--alpha-teal-10)' }}
              >
                <Sparkles className="w-4 h-4" style={{ color: 'var(--alpha-teal)' }} />
              </div>
              <div className="min-w-0 flex-1">
                <h3
                  style={{
                    fontWeight: 'var(--font-weight-semibold)',
                    marginBottom: '4px',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {report.title}
                </h3>
                <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
                  <span
                    className="px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: 'var(--alpha-teal-10)',
                      color: 'var(--alpha-teal)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                    }}
                  >
                    {report.format}
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: 'var(--spectral-violet-5)',
                      color: 'var(--spectral-violet)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                    }}
                  >
                    {report.audience}
                  </span>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                    {report.sections.length} sections · {report.confidence}% confidence
                  </span>
                </div>
              </div>
            </div>
            {!expanded && (
              <p
                className="pl-0 sm:pl-11"
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  lineHeight: '1.5',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {report.executive_summary}
              </p>
            )}
          </div>

          <div className="flex flex-row sm:flex-col items-center sm:items-end gap-3 sm:gap-2 shrink-0 justify-between sm:justify-start">
            <div className="text-left sm:text-right">
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}>
                {dateLabel}
              </p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                {timeLabel}
              </p>
            </div>
            <button
              onClick={() => setExpanded((e) => !e)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors hover:bg-muted whitespace-nowrap"
              style={{
                backgroundColor: 'var(--background)',
                border: '1px solid var(--border)',
                fontSize: 'var(--text-sm)',
                cursor: 'pointer',
              }}
            >
              {expanded
                ? <><ChevronUp   className="w-3.5 h-3.5" /> Collapse</>
                : <><ChevronDown className="w-3.5 h-3.5" /> View Report</>}
            </button>
          </div>
        </div>

        {/* Prompt pill */}
        <div
          className="mt-3 px-3 py-2 rounded-lg"
          style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}
        >
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--muted-foreground)',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>Prompt: </span>
            {report.prompt}
          </p>
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div
          className="px-4 md:px-5 pb-4 md:pb-5 space-y-4"
          style={{ borderTop: '1px solid var(--border)', paddingTop: '16px' }}
        >
          {/* Executive summary */}
          <div
            className="p-4 rounded-xl"
            style={{
              backgroundColor: 'var(--alpha-teal-5)',
              border: '1px solid var(--alpha-teal-12)',
            }}
          >
            <p
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--muted-foreground)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '6px',
              }}
            >
              Executive Summary
            </p>
            <p
              style={{
                fontSize: 'var(--text-sm)',
                lineHeight: '1.6',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {report.executive_summary}
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-3">
            {report.sections.map((s: ReportSection, i: number) => (
              <div
                key={i}
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
              >
                <div className="flex items-start gap-2 mb-2 flex-wrap">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                    style={{
                      backgroundColor: 'var(--alpha-teal-10)',
                      color: 'var(--alpha-teal)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-weight-semibold)',
                    }}
                  >
                    {i + 1}
                  </div>
                  <span
                    style={{
                      fontWeight: 'var(--font-weight-semibold)',
                      fontSize: 'var(--text-sm)',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {s.title}
                  </span>
                  {s.key_metric && (
                    <span
                      className="px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: 'var(--alpha-teal-10)',
                        color: 'var(--alpha-teal)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-semibold)',
                      }}
                    >
                      {s.key_metric}
                    </span>
                  )}
                </div>
                <p
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--muted-foreground)',
                    lineHeight: '1.6',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {s.content}
                </p>
              </div>
            ))}
          </div>

          {/* Key findings */}
          {report.key_findings.length > 0 && (
            <div
              className="p-4 rounded-xl"
              style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
            >
              <h4
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  marginBottom: '8px',
                }}
              >
                Key Findings
              </h4>
              <div className="space-y-1">
                {report.key_findings.map((f, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span style={{ color: 'var(--alpha-teal)', flexShrink: 0 }}>›</span>
                    <p
                      style={{
                        fontSize: 'var(--text-sm)',
                        lineHeight: '1.5',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}
                    >
                      {f}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Export */}
          <div
            className="p-4 rounded-xl"
            style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}
          >
            <p
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                marginBottom: '12px',
              }}
            >
              Export this report
            </p>
            <ExportActions report={report} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// History tab content
// ─────────────────────────────────────────────────────────────────────────────

function HistoryContent({ brandName }: { brandName: string }) {
  const { reports, isLoading, error } = useReportHistory(brandName);
  const [searchTitle,  setSearchTitle]  = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [formatFilter, setFormatFilter] = useState('all');

  const uniqueFormats = useMemo(
    () => Array.from(new Set(reports.map((r) => r.format))).sort(),
    [reports]
  );

  const filtered = useMemo(() => reports.filter((r) => {
    const d = new Date(r.created_at);
    if (searchTitle && !r.title.toLowerCase().includes(searchTitle.toLowerCase()) && !r.prompt.toLowerCase().includes(searchTitle.toLowerCase())) return false;
    if (dateFrom && d < new Date(dateFrom)) return false;
    if (dateTo   && d > new Date(dateTo + 'T23:59:59')) return false;
    if (formatFilter !== 'all' && r.format !== formatFilter) return false;
    return true;
  }), [reports, searchTitle, dateFrom, dateTo, formatFilter]);

  const hasFilters = !!(searchTitle || dateFrom || dateTo || formatFilter !== 'all');

  if (isLoading) return (
    <div className="flex items-center justify-center py-16 md:py-20">
      <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--muted-foreground)' }} />
    </div>
  );

  return (
    <div className="space-y-5 md:space-y-6">
      {/* Filter bar — stacks nicely on mobile */}
      <div
        className="p-3 md:p-4 rounded-xl"
        style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: 'var(--muted-foreground)' }}
            />
            <input
              type="text"
              placeholder="Search by title or prompt..."
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg"
              style={{
                backgroundColor: 'var(--background)',
                border: '1px solid var(--border)',
                fontSize: 'var(--text-sm)',
                outline: 'none',
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <label
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--muted-foreground)',
                whiteSpace: 'nowrap',
              }}
            >
              From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-lg px-3 py-2 flex-1 min-w-0"
              style={{
                backgroundColor: 'var(--background)',
                border: '1px solid var(--border)',
                fontSize: 'var(--text-sm)',
                outline: 'none',
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <label
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--muted-foreground)',
                whiteSpace: 'nowrap',
              }}
            >
              To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-lg px-3 py-2 flex-1 min-w-0"
              style={{
                backgroundColor: 'var(--background)',
                border: '1px solid var(--border)',
                fontSize: 'var(--text-sm)',
                outline: 'none',
              }}
            />
          </div>
          <select
            value={formatFilter}
            onChange={(e) => setFormatFilter(e.target.value)}
            className="rounded-lg px-3 py-2"
            style={{
              backgroundColor: 'var(--background)',
              border: '1px solid var(--border)',
              fontSize: 'var(--text-sm)',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="all">All Formats</option>
            {uniqueFormats.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          {hasFilters && (
            <button
              onClick={() => { setSearchTitle(''); setDateFrom(''); setDateTo(''); setFormatFilter('all'); }}
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--muted-foreground)',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
          {hasFilters && filtered.length !== reports.length && (
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
              {filtered.length} of {reports.length} shown
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3">
          <AlertCircle className="flex-shrink-0 mt-0.5" style={{ color: 'var(--infrared-coral)' }} />
          <p
            style={{
              color: 'var(--infrared-coral)',
              fontSize: 'var(--text-sm)',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {error.message}
          </p>
        </div>
      )}

      {!error && filtered.length === 0 && (
        <div className="text-center py-10 md:py-12">
          <Clock className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--muted-foreground)' }} />
          <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
            {hasFilters ? 'No reports match your filters.' : 'No reports generated yet.'}
          </p>
        </div>
      )}

      {!error && filtered.length > 0 && (
        <div className="space-y-4">
          {filtered.map((report) => <HistoryCard key={report.id} report={report} />)}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

interface ReportGeneratorPageProps {
  brandName:           string;
  brandRunId?:         number | null;
  isSidebarCollapsed?: boolean;
  onSidebarToggle?:    (collapsed: boolean) => void;
}

export function ReportGeneratorPage({
  brandName, brandRunId,
  isSidebarCollapsed = false, onSidebarToggle,
}: ReportGeneratorPageProps) {
  const [activeTab,     setActiveTab]     = useState<'generate' | 'history'>('generate');
  const [prompt,        setPrompt]        = useState('');
  const [selectedIds,   setSelectedIds]   = useState<number[]>([]);
  const [displayReport, setDisplayReport] = useState<GeneratedReport | null>(null);

  const { insights, isLoading: insightsLoading, removeInsight } = useInsights(brandName);
  const { generate, isGenerating, report: generatedReport, error: genError } = useGenerateReport();
  const { reports: historyReports, isLoading: histLoading } = useReportHistory(brandName);
  const reportCount = historyReports.length;

  useEffect(() => {
    if (generatedReport) {
      setDisplayReport(generatedReport);
      setActiveTab('generate');
    }
  }, [generatedReport]);

  const insightsByModule = insights.reduce<Record<string, number>>((acc, ins) => {
    acc[ins.module] = (acc[ins.module] || 0) + 1; return acc;
  }, {});

  const handleGenerate = () => {
    if (!prompt.trim()) return;
    generate({
      brand_name: brandName,
      prompt,
      insight_ids: selectedIds.length > 0 ? selectedIds : undefined,
      brand_run_id: brandRunId,
    });
  };

  const toggleSelect    = (id: number) => setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const handleSelectAll = () => setSelectedIds(insights.map((i) => i.id));
  const handleClearAll  = () => setSelectedIds([]);
  const handleAiPick    = () => setSelectedIds(insights.slice(0, 3).map((i) => i.id));

  const contentMarginClass = isSidebarCollapsed ? 'lg:mr-12' : 'lg:mr-[380px]';
  const showHistoryTab = reportCount > 0 || histLoading;

  const tabs = showHistoryTab
    ? [
        { value: 'generate', label: 'Generate' },
        {
          value: 'history',
          label: 'History',
          labelExtra:
            reportCount > 0
              ? <CountChip count={reportCount} active={activeTab === 'history'} />
              : undefined,
        },
      ]
    : [{ value: 'generate', label: 'Generate' }];

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--background)' }}>
      <div className={`flex-1 min-w-0 transition-all duration-300 ${contentMarginClass}`}>

        {/* ── Page header ──────────────────────────────────────────────── */}
        <div
          className="px-4 md:px-8 py-6 md:py-8 border-b"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}
        >
          <div className="flex items-center gap-2 mb-3 md:mb-4">
            <Sparkles className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--muted-foreground)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              Insights &amp; Reports
            </span>
          </div>
          <h2
            style={{
              fontSize: 'clamp(var(--text-xl), 4.5vw, var(--text-2xl))',
              fontWeight: 'var(--font-weight-semibold)',
              marginBottom: '8px',
              lineHeight: '1.3',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            Assemble intelligence. Generate briefings.
          </h2>
          <p
            style={{
              color: 'var(--muted-foreground)',
              fontSize: 'var(--text-sm)',
              lineHeight: '1.5',
              marginBottom: '16px',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            Save insights from across the platform, then compose polished reports with AI.
          </p>

          {/* Module counts + report pill — wraps cleanly */}
          <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
            {Object.entries(insightsByModule).map(([mod, count]) => (
              <div key={mod} className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: MODULE_COLORS[mod] || 'var(--muted-foreground)' }}
                />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  {count}
                </span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
                  from {mod}
                </span>
              </div>
            ))}
            {insights.length === 0 && !insightsLoading && (
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                No insights yet — save data points from Antenna, Performance, Prompt Intelligence, or Fix Actions.
              </span>
            )}

            {(reportCount > 0 || histLoading) && (
              <div
                className="flex items-center gap-1.5 px-3 py-1 rounded-full"
                style={{
                  backgroundColor: 'var(--alpha-teal-5)',
                  border: '1px solid var(--alpha-teal-12)',
                }}
              >
                <FileText className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--alpha-teal)' }} />
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--alpha-teal)',
                  }}
                >
                  {histLoading ? '…' : reportCount} report{reportCount !== 1 ? 's' : ''} generated
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Tabs — UnderlineTabs primitive ────────────────────────────── */}
        <div className="px-4 md:px-8 py-5 md:py-6">
          <UnderlineTabs
            tabs={tabs}
            value={activeTab}
            onChange={(v) => setActiveTab(v as 'generate' | 'history')}
            size="sm"
            padding="px-0"
          >
            {/* Generate panel */}
            <UnderlineTabsPanel value="generate" className="pt-5 md:pt-6">
              <ReportComposer
                prompt={prompt}
                onPromptChange={setPrompt}
                onGenerate={handleGenerate}
                isGenerating={isGenerating}
                insightCount={insights.length}
                selectedCount={selectedIds.length}
              />

              <div className="mt-5 md:mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
                  <div className="lg:col-span-5 min-w-0">
                    <InsightsList
                      insights={insights}
                      isLoading={insightsLoading}
                      selectedIds={selectedIds}
                      onToggleSelect={toggleSelect}
                      onSelectAll={handleSelectAll}
                      onClearAll={handleClearAll}
                      onSelectAiRecommended={handleAiPick}
                      onDelete={removeInsight}
                    />
                  </div>

                  <div className="lg:col-span-7 min-w-0">
                    {!displayReport ? (
                      <div
                        className="rounded-2xl p-6 md:p-12 text-center"
                        style={{
                          backgroundColor: 'var(--card)',
                          border: '1px solid var(--border)',
                          boxShadow: 'var(--elevation-sm)',
                        }}
                      >
                        <div
                          className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 md:mb-6"
                          style={{ backgroundColor: 'var(--alpha-teal-10)' }}
                        >
                          <Sparkles
                            className="w-8 h-8 md:w-10 md:h-10"
                            style={{ color: 'var(--alpha-teal)' }}
                          />
                        </div>
                        <h3
                          style={{
                            fontSize: 'clamp(var(--text-lg), 3.5vw, var(--text-xl))',
                            fontWeight: 'var(--font-weight-semibold)',
                            marginBottom: '12px',
                          }}
                        >
                          Ready to build your report
                        </h3>
                        <p
                          style={{
                            color: 'var(--muted-foreground)',
                            marginBottom: '24px',
                            lineHeight: '1.6',
                            maxWidth: '440px',
                            margin: '0 auto 24px',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                        >
                          Describe what you need above or pick a quick start. AI will compose a narrative from your saved insights.
                        </p>
                        {genError && (
                          <p
                            style={{
                              color: 'var(--infrared-coral)',
                              fontSize: 'var(--text-sm)',
                              marginBottom: '16px',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                            }}
                          >
                            {genError.message}
                          </p>
                        )}
                        <div
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg"
                          style={{ backgroundColor: 'var(--background)' }}
                        >
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--alpha-teal)' }} />
                          <span style={{ fontSize: 'var(--text-sm)' }}>
                            {insights.length} insights ready to use
                          </span>
                        </div>
                        {reportCount > 0 && (
                          <div className="mt-5 md:mt-6 pt-5 md:pt-6" style={{ borderTop: '1px solid var(--border)' }}>
                            <button
                              onClick={() => setActiveTab('history')}
                              className="flex items-center gap-2 mx-auto transition-colors hover:text-foreground"
                              style={{
                                color: 'var(--muted-foreground)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 'var(--text-sm)',
                              }}
                            >
                              <Clock className="w-4 h-4" />
                              View {reportCount} previously generated report{reportCount !== 1 ? 's' : ''}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <ReportOutput
                        report={displayReport}
                        insightCount={selectedIds.length || insights.length}
                        onReportUpdated={(updated) => setDisplayReport(updated)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </UnderlineTabsPanel>

            {/* History panel */}
            {showHistoryTab && (
              <UnderlineTabsPanel value="history" className="pt-5 md:pt-6">
                <HistoryContent brandName={brandName} />
              </UnderlineTabsPanel>
            )}
          </UnderlineTabs>
        </div>
      </div>

      {/* Generating overlay — viewport-safe sizing */}
      {isGenerating && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 50 }}
        >
          <div
            className="w-full rounded-2xl p-6 md:p-8 text-center"
            style={{
              backgroundColor: 'var(--card)',
              border: '1px solid var(--border)',
              boxShadow: 'var(--elevation-md)',
              maxWidth: 'min(28rem, calc(100dvw - 24px))',
            }}
          >
            <Loader2
              className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 animate-spin"
              style={{ color: 'var(--alpha-teal)' }}
            />
            <h3
              style={{
                fontSize: 'clamp(var(--text-lg), 3.5vw, var(--text-xl))',
                fontWeight: 'var(--font-weight-semibold)',
                marginBottom: '8px',
              }}
            >
              AI Composing Your Story…
            </h3>
            <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
              Analyzing insights and structuring narrative
            </p>
          </div>
        </div>
      )}

      <div className="hidden lg:block">
        <AIAssistantSidebar
          phase="report-generator"
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={onSidebarToggle}
        />
      </div>
    </div>
  );
}